import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useMutation, useQueryClient } from 'react-query';
import { Translate } from '../../../../../../../shared/LanguageService';
import ConfirmDialog from '../../../shared/dialog/view/ConfirmDialog';
import { ICost } from '../../../../../../../shared/localization/currency';
import { useImageApprovalConfirmationText } from '../state/use-image-approval-confirmation-text';
import { IImageThumbnail } from '../../../Entities';
import ImageProcessingApi from '../../../api/ImageProcessingApi';
import { transformExtraImageCost } from '../state/utils';
import { IExtraImageCost, imagesQueryKey } from '../../listImages/queries/use-query-images';
import { photoStatusQueryKey } from '../../entry/queries/use-query-photo-status';

const ApprovalHeader = styled('header')({
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderTopLeftRadius: '1px',
    borderTopRightRadius: '1px',
    padding: '16px'
});

const ApprovalForm = styled('form')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderBottomLeftRadius: '1px',
    borderBottomRightRadius: '1px',
    padding: '16px'
});

type ImagesApprovalBoxProps = {
    extraImageCount: number;
    deliveredCount: number;
    extraImageCost: ICost | null;
    onApprove: () => void;
    isLoading: boolean;
};

function ImagesApprovalBox({
    extraImageCount,
    deliveredCount,
    extraImageCost,
    onApprove,
    isLoading
}: ImagesApprovalBoxProps) {
    let [showConfirmationModal, setShowConfirmationModal] = React.useState(false);
    let confirmModalText = useImageApprovalConfirmationText({ extraImageCount, extraImageCost });

    function handleSubmit(event: React.FormEvent) {
        event.preventDefault();
        setShowConfirmationModal(true);
    }

    function handleApproveByCustomer() {
        onApprove();
        setShowConfirmationModal(false);
    }

    function handleCloseConfirmationModal() {
        setShowConfirmationModal(false);
    }

    return (
        <>
            <Box component="section" aria-labelledby="approve-images-heading">
                <ApprovalHeader>
                    <Typography variant="h2" sx={{ fontSize: '16px' }}>
                        {Translate(
                            'image-processing/page/status-actions/header/customer-approve-edited-images'
                        )}
                    </Typography>
                </ApprovalHeader>
                <ApprovalForm onSubmit={handleSubmit}>
                    <Typography variant="body1" sx={{ fontSize: '14px' }}>
                        {Translate(
                            'image-processing/page/status-actions/description/customer-approve-edited-images'
                        )}
                    </Typography>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={deliveredCount === 0 || isLoading}
                        aria-busy={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
                    >
                        {Translate('image-processing/page/status-actions/button-label/approve')}
                    </Button>
                </ApprovalForm>
            </Box>

            <ConfirmDialog
                open={showConfirmationModal}
                title={Translate(
                    'image-processing/page/status-actions/header/customer-approve-edited-images'
                )}
                text={confirmModalText}
                confirmLabel={Translate(
                    'image-processing/page/status-actions/button-label/approve'
                )}
                cancelLabel={Translate('delete/modal/footer/button-label/cancel')}
                onConfirm={handleApproveByCustomer}
                onCancel={handleCloseConfirmationModal}
            />
        </>
    );
}

type DataLayerProps = {
    orderId: number;
    images: IImageThumbnail[];
    extraImageCount: number;
    deliveredCount: number;
    extraImageCost: IExtraImageCost | null;
    getEffectiveCategory: (image: IImageThumbnail) => number;
    onApprovalStarted?: () => void;
    onApprovalCompleted?: () => void;
    onApprovalFailed?: () => void;
};

export default function DataLayer({
    orderId,
    images,
    extraImageCount,
    deliveredCount,
    extraImageCost,
    getEffectiveCategory,
    onApprovalStarted,
    onApprovalCompleted,
    onApprovalFailed
}: DataLayerProps) {
    let [isApproved, setIsApproved] = React.useState(false);
    let queryClient = useQueryClient();

    let approveMutation = useMutation(
        () => {
            let imagesToApprove = images
                .filter(function (image) {
                    return !image.isCustomerApproved;
                })
                .map(function (image) {
                    return {
                        id: image.id,
                        category: getEffectiveCategory(image)
                    };
                });

            return ImageProcessingApi.customerApproveImages(
                orderId,
                imagesToApprove,
                extraImageCount
            );
        },
        {
            onMutate: function () {
                if (onApprovalStarted) {
                    onApprovalStarted();
                }
            },
            onSuccess: function () {
                queryClient.invalidateQueries(photoStatusQueryKey);
                queryClient.invalidateQueries(imagesQueryKey);
                setIsApproved(true);
                if (onApprovalCompleted) {
                    onApprovalCompleted();
                }
            },
            onError: function () {
                if (onApprovalFailed) {
                    onApprovalFailed();
                }
            }
        }
    );

    return isApproved ? null : (
        <ImagesApprovalBox
            extraImageCount={extraImageCount}
            deliveredCount={deliveredCount}
            extraImageCost={transformExtraImageCost(extraImageCost)}
            onApprove={function handleApprove() {
                return approveMutation.mutateAsync();
            }}
            isLoading={approveMutation.isLoading}
        />
    );
}
